import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { useLanguage } from '../context/LanguageContext';
import { Shield, CreditCard, Truck, XCircle, Lock, Building2, Phone, Mail, MapPin } from 'lucide-react';

export default function LegalPage() {
  const { t } = useLanguage();

  const sections = [
    { icon: Building2, title: t('legal_company_title'), text: t('legal_company_note'), color: 'text-brand-brown' },
    { icon: CreditCard, title: t('legal_payment_title'), text: t('legal_payment_text'), color: 'text-blue-600' },
    { icon: Truck, title: t('legal_delivery_title'), text: t('legal_delivery_text'), color: 'text-brand-orange' },
    { icon: XCircle, title: t('legal_cancel_title'), text: t('legal_cancel_text'), color: 'text-red-500' },
    { icon: Lock, title: t('legal_privacy_title'), text: t('legal_privacy_text'), color: 'text-green-600' },
  ];

  return (
    <div className="min-h-screen bg-brand-cream">
      <Navbar />
      <main className="pt-28 pb-16">
        <div className="max-w-3xl mx-auto px-4">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="w-14 h-14 bg-brand-orange/10 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Shield className="w-7 h-7 text-brand-orange" />
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-brown mb-2" data-testid="legal-title">
              {t('legal_title')}
            </h1>
            <p className="text-brand-muted">{t('legal_subtitle')}</p>
          </div>

          {/* Sections */}
          <div className="space-y-4">
            {sections.map((section, i) => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm border border-brand-border/50" data-testid={`legal-section-${i}`}>
                <div className="flex items-start gap-4">
                  <div className={`w-10 h-10 rounded-lg bg-gray-50 flex items-center justify-center flex-shrink-0 ${section.color}`}>
                    <section.icon className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="font-heading font-bold text-brand-brown text-lg mb-2">{section.title}</h2>
                    <p className="text-brand-muted text-sm leading-relaxed">{section.text}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Contact Card */}
          <div className="mt-8 bg-brand-brown rounded-xl p-6 text-white" data-testid="legal-contact">
            <h2 className="font-heading font-bold text-lg mb-4">{t('legal_contact_title')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div className="space-y-3">
                <p className="font-semibold">KNS TRADING EUROPE</p>
                <p className="text-white/70">КНС ТРЕЙДИНГ ЕВРОПА ЕООД</p>
                <p className="text-white/70">VAT: BG207617173</p>
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-brand-orange" />
                  <a href="mailto:hello@petsbox.eu" className="hover:text-brand-orange transition-colors">hello@petsbox.eu</a>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-brand-orange" />
                  <a href="tel:+359878433982" className="hover:text-brand-orange transition-colors">+359 878 433 982</a>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-brand-orange" />
                  <span className="text-white/70">Sofia district, Opitsvet, str. 23, Bulgaria</span>
                </div>
                <div className="flex items-center gap-3 mt-2">
                  {/* Viber */}
                  <a href="viber://chat?number=+359878433982" className="flex items-center gap-1 text-white/70 hover:text-white">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M11.4 0C9.47.03 5.67.36 3.55 2.28 1.94 3.81 1.34 6.04 1.27 8.8c-.07 2.76-.16 7.94 4.87 9.32v2.13s-.04.86.53 1.04c.69.22 1.09-.44 1.75-1.15.36-.39.86-.96 1.24-1.39 3.41.29 6.03-.37 6.33-.46.7-.22 4.67-.73 5.31-5.98.66-5.41-.32-8.83-2.1-10.37C17.47.41 14.4.02 12.57 0h-1.17z"/></svg>
                    Viber
                  </a>
                  {/* WhatsApp */}
                  <a href="https://wa.me/359878433982" target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-white/70 hover:text-white">
                    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-brand-muted">
            KNS TRADING EUROPE / КНС ТРЕЙДИНГ ЕВРОПА ЕООД &copy; {new Date().getFullYear()} | VAT: BG207617173
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
